import { useEffect, useRef, useState, useCallback } from 'react';
import { Scanner, outline } from '@yudiel/react-qr-scanner';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useMode } from '../stores/mode-context';
import type { Guest } from '../types/guest';

interface DebugEntry {
  time: string;
  text: string;
  extracted: string | null;
  len: number;
}

function extractLink(text: string): string | null {
  var m = text.match(/\/user\/(.+)/i);
  if (m) return m[1].split('?')[0].split('#')[0].trim() || null;
  var seg = text.split('/').pop();
  if (seg && seg.length > 0) return seg.trim();
  return null;
}

export function ScanPage() {
  const navigate = useNavigate();
  const processingRef = useRef(false);
  const lastLinkRef = useRef('');
  const [guest, setGuest] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [manualValue, setManualValue] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [scannerKey, setScannerKey] = useState(0);
  const [debugLog, setDebugLog] = useState<DebugEntry[]>([]);
  const { registrationEnabled, syncRegistration } = useMode();

  // Self-healing: if server rejects due to mode mismatch, sync local flag immediately
  function syncModeFromError(err: any) {
    var msg = String((err && err.message) || '');
    if (msg.indexOf('Registration is disabled') !== -1) syncRegistration(false);
    else if (msg.indexOf('not available during registration') !== -1) syncRegistration(true);
  }

  useEffect(() => {
    if (!guest) return;
    setDebugLog([]);
  }, [guest]);

  async function logScan(text: string) {
    const extracted = extractLink(text);
    const entry: DebugEntry = { time: new Date().toLocaleTimeString(), text, extracted, len: text.length };
    setDebugLog(function (prev) { return prev.slice(-9).concat([entry]); });
    try {
      await fetch('/api/debug/scan-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, extracted, len: text.length }),
      });
    } catch {}
  }

  const handleScan = useCallback(async function (detectedCodes: { rawValue: string }[]) {
    if (processingRef.current || !detectedCodes.length) return;
    var text = detectedCodes[0].rawValue;
    if (!text) return;
    logScan(text);
    var link = extractLink(text);
    if (!link || link === lastLinkRef.current) return;
    lastLinkRef.current = link;
    processingRef.current = true;
    setGuest(null);
    setMessage(null);
    setLoading(true);
    try {
      var res = await api.user.get(link);
      if (res.guest) {
        setGuest(res.guest as Guest);
      } else {
        setMessage({ text: 'Участник не найден', type: 'error' });
        processingRef.current = false;
      }
    } catch {
      setMessage({ text: 'Ошибка загрузки данных', type: 'error' });
      processingRef.current = false;
    } finally {
      setLoading(false);
    }
  }, []);

  async function handleRegister() {
    if (!guest || !guest.directus_id) return;
    try {
      var res = await api.user.register(guest.directus_id);
      setMessage({ text: res.message, type: res.success ? 'success' : 'error' });
      if (res.success) {
        setGuest(Object.assign({}, guest, { registered: true, times: new Date().toISOString() }));
      }
    } catch (err: any) {
      syncModeFromError(err);
      setMessage({ text: err.message || 'Ошибка сервера', type: 'error' });
    }
  }

  async function handleDinner() {
    if (!guest || !guest.directus_id) return;
    try {
      var res = await api.user.dinner(guest.directus_id);
      setMessage({ text: res.message, type: res.success ? 'success' : 'error' });
      if (res.success) {
        setGuest(Object.assign({}, guest, { dinner: true }));
      }
    } catch (err: any) {
      syncModeFromError(err);
      setMessage({ text: err.message || 'Ошибка сервера', type: 'error' });
    }
  }

  function dismiss() {
    setGuest(null);
    setMessage(null);
    processingRef.current = false;
    lastLinkRef.current = '';
  }

  async function handleManualSubmit() {
    if (!manualValue.trim()) return;
    var link = extractLink(manualValue.trim());
    if (!link) {
      setMessage({ text: 'Некорректная ссылка или UUID', type: 'error' });
      return;
    }
    setShowManual(false);
    setManualValue('');
    lastLinkRef.current = link;
    setMessage(null);
    setLoading(true);
    try {
      var res = await api.user.get(link);
      if (res.guest) {
        setGuest(res.guest as Guest);
      } else {
        setMessage({ text: 'Участник не найден', type: 'error' });
      }
    } catch {
      setMessage({ text: 'Ошибка загрузки данных', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function retry() {
    setCameraError(null);
    setScannerKey(function (k) { return k + 1; });
  }

  var isRegistered = guest ? guest.registered : false;
  var hasPaid = guest ? guest.pay_date : false;
  var canRegister = hasPaid && !isRegistered && registrationEnabled;
  var canMarkDinner = hasPaid && guest && !guest.dinner && !registrationEnabled;

  var guestCard = null;
  if (guest) {
    guestCard = (
      <div className="absolute bottom-0 left-0 right-0 z-20 max-h-[75dvh] overflow-y-auto rounded-t-2xl bg-ct-surface border-t border-ct-border px-6 pb-[max(2rem,env(safe-area-inset-bottom,1rem))] pt-5 shadow-ct-md">
        <div className="mb-4 text-center">
          <h2 className="text-xl font-bold text-ct-text tracking-tight">
            {guest.l_name} {guest.f_name}
          </h2>
          {guest.company ? <p className="text-sm text-ct-text-muted">{guest.company}</p> : null}
          {guest.country ? <p className="text-xs text-ct-text-muted">{guest.country}</p> : null}
        </div>
        <div className="mb-4 space-y-2">
          <Row label="Оплата" value={hasPaid ? 'Оплачено' : 'Не оплачено'} valueClass={hasPaid ? 'text-emerald-600' : 'text-red-500'} />
          <Row label="Регистрация" value={isRegistered ? 'Зарегистрирован' : 'Не зарегистрирован'} valueClass={isRegistered ? 'text-emerald-600' : 'text-ct-text-muted'} />
          <Row label="Ужин" value={guest.dinner ? 'Отмечен' : 'Не отмечен'} valueClass={guest.dinner ? 'text-emerald-600' : 'text-ct-text-muted'} />
        </div>
        {message ? (
          <div className={'mb-3 rounded-lg p-2.5 text-center text-sm border ' + (message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200')}>
            {message.text}
          </div>
        ) : null}
        <div className="space-y-2">
          {canRegister ? (
            <button onClick={handleRegister} className="w-full rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary py-2.5 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]">
              Зарегистрировать
            </button>
          ) : null}
          {canMarkDinner ? (
            <button onClick={handleDinner} className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 py-2.5 text-sm font-bold text-white shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]">
              Отметить ужин
            </button>
          ) : null}
          {!canRegister && !canMarkDinner ? (
            <div className="rounded-lg bg-slate-50 p-3 text-center text-sm text-ct-text-muted border border-ct-border">
              {!hasPaid
                ? 'Действия недоступны — оплата не подтверждена'
                : registrationEnabled
                  ? 'Гость зарегистрирован'
                  : 'Ужин уже отмечен'}
            </div>
          ) : null}
          <button onClick={dismiss} className="w-full rounded-lg border border-ct-border bg-ct-surface py-2.5 text-sm font-semibold text-ct-text-muted transition-all duration-200 hover:bg-indigo-50 hover:text-ct-primary active:scale-[0.98]">
            Сканировать следующего
          </button>
        </div>
      </div>
    );
  }

  var debugPanel = null;
  if (debugLog.length > 0 && !guest) {
    debugPanel = (
      <div className="absolute left-2 right-2 top-2 z-20 max-h-48 overflow-y-auto rounded-lg bg-black/70 p-2 font-mono text-[10px] leading-tight text-emerald-300">
        {debugLog.map(function (e, i) {
          return (
            <div key={i} className="mb-1 border-b border-white/10 pb-1 last:border-0">
              <span className="text-white/50">{e.time}</span>
              <span className={e.extracted ? 'text-emerald-300' : 'text-amber-300'}>
                [{e.len}] {e.extracted || '(bad)'}
              </span>
              <div className="truncate text-white/40">{e.text}</div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] w-screen flex-col overflow-hidden bg-black">
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <button
          onClick={function () { navigate(-1); }}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-all duration-200 hover:bg-white/30"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-sm font-medium text-white/80">Сканирование QR</span>
        <button
          onClick={function () { setShowManual(function (p) { return !p; }); setMessage(null); }}
          className="rounded-full bg-white/20 px-3 py-1.5 text-xs text-white transition-all duration-200 hover:bg-white/30"
        >
          {showManual ? 'Камера' : 'Ввести код'}
        </button>
      </div>
      <div className="relative flex-1">
        {cameraError ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6">
            <p className="mb-6 text-center text-sm text-white/70">{cameraError}</p>
            <button
              onClick={retry}
              className="rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-8 py-3 text-base font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
            >
              Попробовать снова
            </button>
            <button
              onClick={function () { setShowManual(true); }}
              className="mt-4 text-sm text-white/50 underline"
            >
              Ввести код вручную
            </button>
          </div>
        ) : (
          <Scanner
            key={scannerKey}
            onScan={handleScan}
            onError={function (error) {
              if (error && error.kind === 'permission-denied') {
                setCameraError('Доступ к камере запрещён. Разрешите доступ в настройках браузера или используйте ручной ввод.');
              } else if (error && error.kind === 'no-camera') {
                setCameraError('Камера не найдена. Используйте ручной ввод.');
              } else {
                setCameraError('Не удалось запустить камеру. Используйте ручной ввод.');
              }
            }}
            constraints={{ facingMode: 'environment' }}
            formats={['qr_code']}
            components={{ tracker: outline }}
            styles={{
              container: { height: '100%', width: '100%' },
              video: { objectFit: 'cover' },
            }}
            scanDelay={500}
          />
        )}
        {showManual ? (
          <div className="absolute left-4 right-4 top-4 z-10">
            <div className="rounded-xl bg-ct-surface border border-ct-border p-4 shadow-ct-md">
              <input
                value={manualValue}
                onChange={function (e) { setManualValue(e.target.value); }}
                placeholder="Ссылка или UUID участника"
                className="w-full rounded-lg border border-ct-border bg-ct-surface px-4 py-2.5 text-sm text-ct-text placeholder:text-ct-text-muted/50 focus:ring-2 focus:ring-ct-primary focus:ring-offset-1 focus:border-ct-primary focus:outline-none transition-all duration-200"
                onKeyDown={function (e) { if (e.key === 'Enter') handleManualSubmit(); }}
                autoFocus
              />
              <button
                onClick={handleManualSubmit}
                className="mt-2 w-full rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary py-2 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
              >
                Найти
              </button>
            </div>
          </div>
        ) : null}
        {loading ? (
          <div className="absolute left-1/2 top-1/3 z-10 -translate-x-1/2">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        ) : null}
        {!guest && !cameraError && !showManual ? (
          <div className="absolute bottom-6 left-0 right-0 z-10 text-center pointer-events-none">
            <p className="text-sm text-white/50">Наведите камеру на QR-код</p>
          </div>
        ) : null}
        {debugPanel}
        {guestCard}
      </div>
    </div>
  );
}

function Row(props: { label: string; value: string; valueClass: string }) {
  return (
    <div className="flex justify-between rounded-lg bg-slate-50 px-4 py-2.5 border border-ct-border">
      <span className="text-sm text-ct-text-muted">{props.label}</span>
      <span className={'text-sm font-semibold ' + props.valueClass}>{props.value}</span>
    </div>
  );
}
